import { observer } from "mobx-react-lite";
import bodyStylesManager from "../../../stores/body-styles-manager";
import contentManager from "../../../stores/content-manager";
import SatoriDimensionsBox from "../shared/satori-ui/dimensions-box";
import { ITextBlockData } from "../../../types/content-block-data";
import type {TextTransformType, ITypographyRole} from "../../../stores/state-interfaces";
import { cleanDimenValues } from "../../../lib/page-builder-lib";
import glideBarManager from "../../../stores/glidebar-manager";
import SatoriColorPicker from "../shared/satori-ui/colorpicker";
import SatoriDropDownList from "../shared/satori-ui/dropdownlist";
import { STANDARD_FONT_FAMILIES, FONT_WEIGHTS, ALIGN_OPTIONS, TEXT_DECORATIONS, TEXT_TRANSFORMATIONS, NOPERCENT_UNITS } from "../../../types/constants";
import contentHistoryManager from "../../../stores/content-history-manager";

/**
 * TextStyles – Sidebar Editing Panel for Text Blocks
 *
 * Provides a sidebar panel for configuring typography and layout properties
 * of a Text content block in the Satori PageBuilder. Supports color, font family,
 * font size, weight, line height, letter spacing, text decoration, text transform, and alignment.
 *
 * @component
 * @category OptionsPanel
 *
 * @param {Object} props - Component props.
 * @param {string} props.blockId - The unique ID of the Text content block to edit.
 *
 * @example
 * // Render text styles panel for a given Text block
 * <TextStyles blockId={blockId} />
 *
 * @remarks
 * - Fetches and updates the target Text block's data from the MobX contentManager store.
 * - Renders grouped UI controls for all editable Text fields, including typography and alignment.
 * - Dimension fields (font size, line height, letter spacing) support px, rem, and em units with automatic conversion.
 * - All changes call `contentManager.setTextConfig` for atomic and reactive MobX state updates.
 * - Mirrors the options panel structure of HeadingStyles, ButtonOptions, and ColumnsOptions for consistency.
 * - Fully reactive via MobX's `observer()` wrapper.
 *
 * @see contentManager.setTextConfig
 * @see HeadingStyles, ButtonOptions, ColumnsOptions – For parallel sidebar editing patterns
 */
export default observer(function TextStyles({ blockId }: { blockId: string }) {
	const block = contentManager.getContentBlockById(blockId);
	console.log("blockId:", block?.contentBlockId);
	
	if (!block || block.blockType !== "text") return null;

	const textData = block.blockData as ITextBlockData;

	const parentFontSize = bodyStylesManager.getBodyStylesState().typography!["p"].fontSize;
		let parentRawFontSize: number = 0;  // We will always convert this into pixels first (if necessary)
	
		if (parentFontSize?.unit === "rem" || parentFontSize?.unit === "em")
			parentRawFontSize = parseFloat(parentFontSize!.value) * 16;
		else
			parentRawFontSize = parseInt(parentFontSize!.value);

	// Handler to update blockData
	function update(field: keyof ITextBlockData, value: any) {
		contentManager.setTextConfig(blockId, {
			...textData,
			[field]: value,
		});
	}

	// --- UI ---
	return (
    <section className="flex flex-col gap-3 p-4">
      {/* Font Family */}
      <SatoriDropDownList
        label={"Font Family"}
        value={textData.fontFamily || "Inter, sans-serif"}
        isFontFamily={true}
        onChange={(val) => update("fontFamily", val)}
      />

      {/* Font Size */}
      <SatoriDimensionsBox
        label={"Font Size"}
        value={cleanDimenValues(textData.fontSize?.value!) || "2"}
        unit={textData.fontSize?.unit || "rem"}
        onValueChange={(val) =>
          update("fontSize", {
            value: val,
            unit: textData.fontSize?.unit || "rem",
          })
        }
        onUnitChange={(unitVal) => {
          if (unitVal === "px") {
            // We need to change from rem/em value to pixel value
            update("fontSize", {
              value:
                Math.round(
                  parseFloat(textData?.fontSize?.value!) * parentRawFontSize
                ).toString() || "",
              unit: unitVal,
            });
          } else if (unitVal === "rem" || unitVal === "em") {
            // We need to change from pixel value to rem/em value
            update("fontSize", {
              value:
                (
                  parseFloat(textData?.fontSize?.value!) / parentRawFontSize
                ).toString() || "",
              unit: unitVal,
            });
          }
        }}
      />

      {/* Text Color */}
      <SatoriColorPicker
        label={"Text"}
        value={textData.color || "#222222"}
        onChange={(val) => update("color", val)}
      />

      {/* Font Weight */}
      <SatoriDropDownList
        layout="horizontal"
        label={"Font Weight"}
        options={FONT_WEIGHTS}
        isFontFamily={false}
        value={textData.fontWeight || "500"}
        onChange={(val) => update("fontWeight", val)}
      />

      {/* Line Height */}
      <SatoriDimensionsBox
        label={"Line Height"}
        value={cleanDimenValues(textData.lineHeight?.value!) || "1.2"}
        unit={textData.lineHeight?.unit || ""}
        units={NOPERCENT_UNITS}
        onValueChange={(val) =>
          update("lineHeight", {
            value: val,
            unit: textData.lineHeight?.unit || "",
          })
        }
        onUnitChange={(unitVal) => {
          if (unitVal === "px") {
            // We need to change from rem/em value to pixel value
            update("lineHeight", {
              value:
                Math.round(
                  parseFloat(textData?.lineHeight?.value!) * parentRawFontSize
                ).toString() || "",
              unit: unitVal,
            });
          } else if (unitVal === "rem" || unitVal === "em") {
            // We need to change from pixel value to rem/em value
            update("lineHeight", {
              value:
                (
                  parseFloat(textData?.lineHeight?.value!) / parentRawFontSize
                ).toString() || "",
              unit: unitVal,
            });
          }
        }}
      />

      {/* Letter Spacing */}
      <SatoriDimensionsBox
				label={"Letter Spacing"}
        value={cleanDimenValues(textData.letterSpacing?.value!) || "0"}
        unit={textData.letterSpacing?.unit || "em"}
        onValueChange={(val) =>
          update("letterSpacing", {
            value: val,
            unit: textData.letterSpacing?.unit || "em",
          })
        }
        onUnitChange={(unitVal) => {
          if (unitVal === "px") {
            // We need to change from rem/em value to pixel value
            update("letterSpacing", {
              value:
                Math.round(
                  parseFloat(textData?.letterSpacing?.value!) *
                    parentRawFontSize
                ).toString() || "",
              unit: unitVal,
            });
          } else if (unitVal === "rem" || unitVal === "em") {
            // We need to change from pixel value to rem/em value
            update("letterSpacing", {
              value:
                (
                  parseFloat(textData?.letterSpacing?.value!) /
                  parentRawFontSize
                ).toString() || "",
              unit: unitVal,
            });
          }
        }}
      />

      {/* Text Decoration */}
      <SatoriDropDownList
        layout="horizontal"
        label={"Text Decoration"}
        options={TEXT_DECORATIONS}
        isFontFamily={false}
        value={textData.textDecoration || "none"}
        onChange={(val) => update("textDecoration", val)}
      />

      {/* Text Transform */}
      <SatoriDropDownList
        layout="horizontal"
        label={"Text Transform"}
        options={TEXT_TRANSFORMATIONS}
        isFontFamily={false}
        value={textData.textTransform || "none"}
        onChange={(val) => update("textTransform", val)}
      />

      {/* Alignment */}
      <SatoriDropDownList
        layout="horizontal"
        label={"Alignment"}
        options={ALIGN_OPTIONS}
        isFontFamily={false}
        value={textData.alignment || "none"}
        onChange={(val) => update("alignment", val)}
      />
    </section>
  );
});
